/**
 * Single source of truth mapping every English route to its Spanish equivalent.
 *
 * Used by:
 *   - LangToggle.jsx          (flips the user between EN <-> ES on the same logical page)
 *   - SpanishHomePage.js etc. (build hreflangEn URL for <EsSEO />)
 *   - sitemap-es generator    (Phase 4 — derives ES URL set from this map)
 *
 * Keep slugs Mexican-American Spanish per FORTWORTHDENTAL_BILINGUAL_BRIEF.md.
 * Always lowercase, no trailing slash.
 */

// EN path -> ES path
export const ROUTE_MAP_EN_TO_ES = {
  '/': '/es',

  // Core
  '/about-us': '/es/sobre-nosotros',
  '/our-team': '/es/nuestro-equipo',
  '/our-offices': '/es/nuestras-oficinas',
  '/contact-us': '/es/contacto',
  '/testimonials': '/es/testimonios',
  '/form-finances': '/es/financiamiento',
  '/financing': '/es/financiamiento',
  '/areas-we-serve': '/es/areas-que-atendemos',
  '/press-releases': '/es/comunicados-de-prensa',

  // Services (root-level, mirroring WordPress)
  '/dental-implants': '/es/implantes-dentales',
  '/invisalign': '/es/invisalign',
  '/lanap': '/es/lanap',
  '/cosmetic-dentistry': '/es/odontologia-cosmetica',
  '/emergency-dentist': '/es/dentista-de-emergencia',
  '/bone-grafting': '/es/injerto-de-hueso',
  '/bruxism-teeth-grinding': '/es/bruxismo',
  '/composite-fillings': '/es/empastes',
  '/crowns-bridges': '/es/coronas-y-puentes',
  '/dentures': '/es/dentaduras-postizas',
  '/gingivectomy': '/es/gingivectomia',
  '/guided-tissue-bone-regeneration': '/es/regeneracion-tisular-y-osea',
  '/gum-disease': '/es/enfermedad-de-las-encias',
  '/laser-dentistry': '/es/odontologia-laser',
  '/occlusal-bite-adjustment': '/es/ajuste-de-mordida',
  '/oral-cancer-screening': '/es/deteccion-de-cancer-oral',
  '/osseous-surgery': '/es/cirugia-osea',
  '/porcelain-veneers': '/es/carillas-de-porcelana',
  '/first-fit-veneers': '/es/carillas-first-fit',
  '/preventative-dentistry': '/es/odontologia-preventiva',
  '/ridge-augmentation': '/es/aumento-de-cresta',
  '/ridge-preservation': '/es/preservacion-de-cresta',
  '/root-canals': '/es/endodoncia',
  '/scaling-root-planing': '/es/limpieza-profunda',
  '/sedation': '/es/sedacion-dental',
  '/teeth-whitening': '/es/blanqueamiento-dental',

  // Specials
  '/specials': '/es/promociones',
  '/dental-implants-free-consult-with-x-rays': '/es/promociones/implantes-consulta-gratis',
  '/invisalign-free-consultation': '/es/promociones/invisalign-consulta-gratis',
  '/179-healthy-cleaning-exam-and-set-of-x-rays': '/es/promociones/limpieza-179',
  '/99-emergency-exam-and-x-ray-affordable-dental-services': '/es/promociones/emergencia-99',
  '/free-lanap-consultation': '/es/promociones/lanap-consulta-gratis',

  // Implant Center
  '/implant-center': '/es/centro-de-implantes',
  '/implant-center/dental-implants': '/es/centro-de-implantes/implantes-dentales',
  '/implant-center/mini-dental-implants': '/es/centro-de-implantes/mini-implantes',
  '/implant-center/jawbone-grafting-and-sinus-lift': '/es/centro-de-implantes/injerto-mandibular',
  '/implant-center/cosmetic-dentistry-at-fort-worth-dental': '/es/centro-de-implantes/odontologia-cosmetica',
  '/implant-center/fort-worth-dental-provides-a-dental-plan-you-can-afford': '/es/centro-de-implantes/plan-dental',

  // SEO landing
  '/dental-implant-cost-fort-worth': '/es/costo-implantes-dentales-fort-worth',
  '/affordable-dental-implants-fort-worth': '/es/implantes-dentales-economicos-fort-worth',
  '/invisalign-cost-fort-worth': '/es/costo-invisalign-fort-worth',
  '/top-dentists-fort-worth': '/es/mejores-dentistas-fort-worth',
  '/dental-implants-fort-worth': '/es/implantes-dentales-fort-worth',
  '/dentist-near-me': '/es/dentista-cerca-de-mi',
  '/best-dentist-fort-worth': '/es/mejor-dentista-fort-worth',
  '/best-dentist-near-me': '/es/mejor-dentista-cerca-de-mi',

  // Locations
  '/dentist-near-benbrook': '/es/dentista-cerca-de-benbrook',
  '/dentist-near-aledo': '/es/dentista-cerca-de-aledo',
  '/dentist-near-westover-hills': '/es/dentista-cerca-de-westover-hills',

  // Blog
  '/blogs': '/es/blog',
  '/blogs/archive': '/es/blog/archivo',

  // Legal
  '/terms': '/es/terminos',
  '/privacy': '/es/privacidad',
  '/hipaa-npp': '/es/hipaa',
  '/sitemap': '/es/mapa-del-sitio',
};

// Reverse: ES path -> EN path
export const ROUTE_MAP_ES_TO_EN = Object.fromEntries(
  Object.entries(ROUTE_MAP_EN_TO_ES).map(([en, es]) => [es, en])
);

// ES-only hubs that have NO direct EN counterpart. Toggling these from ES
// back to EN should land on the closest EN equivalent (typically homepage
// or a hub that does exist in EN).
const ES_ONLY_FALLBACK = {
  '/es/servicios': '/',  // EN has no /services index — services live at root level
};

// Blog post slug map, populated dynamically by SpanishBlogPost when it
// fetches a post. ES_slug -> EN_slug. Lets the LangToggle on
// /es/blog/<es_slug> navigate to /blogs/<en_slug> (otherwise the naive
// path-prefix swap would 404).
const BLOG_SLUG_ES_TO_EN = {};
export const registerBlogSlugMapping = (esSlug, enSlug) => {
  if (esSlug && enSlug) {
    BLOG_SLUG_ES_TO_EN[esSlug] = enSlug;
  }
};

// Service-area service-slug map (EN -> ES). Mirrors ES_SERVICE_MAP in
// /app/backend/routes/service_areas.py — keep these in sync.
const SERVICE_AREA_EN_TO_ES = {
  'dental-implants': 'implantes-dentales',
  'lanap-gum-treatment': 'lanap-tratamiento-encias',
  'invisalign': 'invisalign',
  'cosmetic-dentistry': 'odontologia-cosmetica',
  'emergency-dentist': 'dentista-de-emergencia',
  'general-dentistry': 'odontologia-general',
  'teeth-whitening': 'blanqueamiento-dental',
  'porcelain-veneers': 'carillas-de-porcelana',
  'crowns-bridges': 'coronas-y-puentes',
  'dentures': 'dentaduras-postizas',
  'root-canals': 'endodoncia',
};
const SERVICE_AREA_ES_TO_EN = Object.fromEntries(
  Object.entries(SERVICE_AREA_EN_TO_ES).map(([en, es]) => [es, en])
);

// Cache for dynamic service-area slugs registered at runtime by
// SpanishServiceAreaPage / ServiceAreaPage when they fetch a record.
const SERVICE_AREA_SLUG_EN_TO_ES = {};
const SERVICE_AREA_SLUG_ES_TO_EN = {};
export const registerServiceAreaSlugMapping = (enSlug, esSlug) => {
  if (enSlug && esSlug) {
    SERVICE_AREA_SLUG_EN_TO_ES[enSlug] = esSlug;
    SERVICE_AREA_SLUG_ES_TO_EN[esSlug] = enSlug;
  }
};

/** Try to translate an EN service-area slug into its ES equivalent purely
 * from the static service-slug map. e.g., 'dental-implants-arlington' ->
 * 'implantes-dentales-arlington'. Returns null if no match found. */
function deriveEsServiceAreaSlug(enSlug) {
  if (!enSlug) return null;
  if (SERVICE_AREA_SLUG_EN_TO_ES[enSlug]) return SERVICE_AREA_SLUG_EN_TO_ES[enSlug];
  // Find the longest matching service prefix
  for (const enService of Object.keys(SERVICE_AREA_EN_TO_ES)) {
    const prefix = enService + '-';
    if (enSlug.startsWith(prefix)) {
      const city = enSlug.slice(prefix.length);
      return `${SERVICE_AREA_EN_TO_ES[enService]}-${city}`;
    }
  }
  return null;
}
function deriveEnServiceAreaSlug(esSlug) {
  if (!esSlug) return null;
  if (SERVICE_AREA_SLUG_ES_TO_EN[esSlug]) return SERVICE_AREA_SLUG_ES_TO_EN[esSlug];
  for (const esService of Object.keys(SERVICE_AREA_ES_TO_EN)) {
    const prefix = esService + '-';
    if (esSlug.startsWith(prefix)) {
      const city = esSlug.slice(prefix.length);
      return `${SERVICE_AREA_ES_TO_EN[esService]}-${city}`;
    }
  }
  return null;
}

/**
 * Resolve the ES counterpart for any English path.
 * Falls back to "/es" + path if the path isn't in the explicit map (e.g., dynamic blog slugs).
 */
export function toEs(enPath) {
  if (!enPath) return '/es';
  if (enPath in ROUTE_MAP_EN_TO_ES) return ROUTE_MAP_EN_TO_ES[enPath];
  // Dynamic blog post: /blogs/<slug>  ->  /es/blog/<slug>
  if (enPath.startsWith('/blogs/')) return enPath.replace(/^\/blogs\//, '/es/blog/');
  // Dynamic service-area page: /<service>-<city>  ->  /es/<service-es>-<city>
  const enSlug = enPath.replace(/^\//, '');
  const esSlug = deriveEsServiceAreaSlug(enSlug);
  if (esSlug) return `/es/${esSlug}`;
  return '/es' + enPath;
}

/**
 * Resolve the EN counterpart for any Spanish path.
 */
export function toEn(esPath) {
  if (!esPath) return '/';
  if (esPath in ES_ONLY_FALLBACK) return ES_ONLY_FALLBACK[esPath];
  if (esPath in ROUTE_MAP_ES_TO_EN) return ROUTE_MAP_ES_TO_EN[esPath];
  // Blog post — use the registered ES->EN slug mapping if SpanishBlogPost has populated it
  if (esPath.startsWith('/es/blog/')) {
    const esSlug = esPath.replace(/^\/es\/blog\//, '');
    const enSlug = BLOG_SLUG_ES_TO_EN[esSlug] || esSlug;
    return `/blogs/${enSlug}`;
  }
  // Dynamic service-area page: /es/<service-es>-<city>  ->  /<service-en>-<city>
  if (esPath.startsWith('/es/')) {
    const esSlug = esPath.replace(/^\/es\//, '');
    const enSlug = deriveEnServiceAreaSlug(esSlug);
    if (enSlug) return `/${enSlug}`;
  }
  return esPath.replace(/^\/es/, '') || '/';
}
